import {
  AssessmentAnswer,
  AssessmentQuestionType,
  MultipleChoiceScoredAssessmentAnswer,
  MultipleChoiceAssessmentAnswer,
  MultiSelectAssessmentAnswer,
  FreeTextAssessmentAnswer,
  RichTextAssessmentAnswer,
  SignatureAssessmentAnswer,
  MultiSignatureAssessmentAnswer,
  FileUploadAssessmentAnswer,
  PatientContactAssessmentAnswer,
} from '../../../../generated/graphql';
import { getAnswerQuestion } from '../../../lib/answerHelpers';

/**
 * Filters out empty follow-up answers from a list of assessment answers.
 *
 * ConditionalMultipleChoice questions are stored as two plain MultipleChoice
 * entries in sequence: the parent question and a follow-up. When the user
 * answered the parent in a way that does not trigger the follow-up, the
 * follow-up entry is still persisted but with an empty answer. This helper
 * removes those empty rows so provider-facing views and PDF exports don't
 * display blank follow-up questions. Legitimate empty answers on non-follow-up
 * questions are kept.
 *
 * Matching strategy (in priority order):
 *   1. Structural — if an `questions` template is provided, collect every
 *      `followUp.question` text from ConditionalMultipleChoice entries and use
 *      that set for membership testing. This is exact and doesn't rely on
 *      question-text conventions.
 *   2. Heuristic fallback — if no template is available, the legacy prefix
 *      `"If Yes"` is used for backward compatibility.
 *
 * The generic parameter T is unbounded so the function accepts both the GQL
 * AssessmentAnswer union (where `question` and `answer` live on subtypes) and
 * plain test objects. Field access is done via safe property lookup.
 */
export const filterEmptyFollowUpAnswers = <T extends object>(
  answers: T[],
  questions?: Array<{
    __typename?: string;
    questionType?: string;
    question?: string;
    followUp?: { question?: string };
  }>,
): T[] => {
  // Build a set of follow-up question texts from the template when available.
  // Only use the structural mode if the template actually contains at least one
  // ConditionalMultipleChoice — otherwise fall back to the heuristic so we
  // don't silently stop filtering legacy-form empty follow-ups (pre-existing
  // templates with 'If Yes…' prefixed questions that never got the new type).
  let followUpQuestionTexts: Set<string> | null = null;
  if (questions && questions.length > 0) {
    const gathered = new Set<string>();
    for (const q of questions) {
      if (
        q.questionType === AssessmentQuestionType.ConditionalMultipleChoice &&
        q.followUp?.question
      ) {
        gathered.add(q.followUp.question);
      }
    }
    if (gathered.size > 0) {
      followUpQuestionTexts = gathered;
    }
  }

  return answers.filter((a) => {
    const rec = a as Record<string, unknown>;
    const question = typeof rec['question'] === 'string' ? rec['question'] : '';
    const answer = rec['answer'];
    const isEmpty = !answer;

    let isFollowUp: boolean;
    if (followUpQuestionTexts !== null) {
      // Structural match — prefer this when the template is available AND
      // contains at least one ConditionalMultipleChoice question.
      isFollowUp = followUpQuestionTexts.has(question);
    } else {
      // Heuristic fallback for call sites that don't have the template, or
      // for templates whose questions predate the new type.
      isFollowUp = question.startsWith('If Yes');
    }

    return !(isFollowUp && isEmpty);
  });
};

export const findAnswerForQuestion = (
  answers: AssessmentAnswer[],
  question: { question?: string; questionType: AssessmentQuestionType },
): AssessmentAnswer | undefined => {
  // Scan from the end to prioritize the latest answer (in case of duplicates)
  return answers
    .slice()
    .reverse()
    .find((ans) => {
      switch (ans.questionType) {
        case AssessmentQuestionType.MultipleChoiceScored:
          return (
            (ans as MultipleChoiceScoredAssessmentAnswer).question ===
            question.question
          );
        case AssessmentQuestionType.MultipleChoice:
          return (
            (ans as MultipleChoiceAssessmentAnswer).question ===
            question.question
          );
        case AssessmentQuestionType.MultiSelect:
          return (
            (ans as MultiSelectAssessmentAnswer).question === question.question
          );
        case AssessmentQuestionType.FreeText:
          return (
            (ans as FreeTextAssessmentAnswer).question === question.question
          );
        case AssessmentQuestionType.RichText:
          return (
            (ans as RichTextAssessmentAnswer).question === question.question
          );
        case AssessmentQuestionType.Initials:
          return (
            (ans as SignatureAssessmentAnswer).question === question.question
          );
        case AssessmentQuestionType.Signature:
          return (
            (ans as SignatureAssessmentAnswer).question === question.question
          );
        case AssessmentQuestionType.MultiSignature:
          return (
            (ans as MultiSignatureAssessmentAnswer).question ===
            question.question
          );
        case AssessmentQuestionType.FileUpload:
          return (
            (ans as FileUploadAssessmentAnswer).question === question.question
          );
        case AssessmentQuestionType.Autofill:
        case AssessmentQuestionType.DatePicker:
          // Use utility function for type-safe question field access
          return getAnswerQuestion(ans) === question.question;
        case AssessmentQuestionType.PatientContact:
          return (
            (ans as PatientContactAssessmentAnswer).question ===
            question.question
          );
        default:
          return false;
      }
    });
};
